import { diffDays, formatTokens, isSameDay, parseDate, startOf } from 'helpers/date_utils';

import type { Activity, StageTimelineItem, TimelineStageEvent } from './types';

export interface TimelineGroup<T = Activity> {
  label: string;
  activities: T[];
}

// Activity dates are calendar dates ("2026-09-27"), compared against the viewer's local today
export function timelineGroupLabel(date: string, now: Date = new Date()): string {
  const day = parseDate(date);
  if (!day) return 'Undated';

  const today = startOf(now, 'day');

  if (day > today) return 'Upcoming';
  if (day.getTime() === today.getTime()) return 'Today';
  // A rolling week, so Monday mornings don't push yesterday under "Earlier this month"
  if (diffDays(today, day) < 7) return 'This week';
  if (day >= startOf(today, 'month')) return 'Earlier this month';
  if (day >= startOf(today, 'year')) return 'Earlier this year';

  return String(day.getFullYear());
}

// Expects activities already in timeline order (newest first), so each label forms one run
export function groupByPeriod<T extends { date: string }>(activities: T[], now: Date = new Date()): TimelineGroup<T>[] {
  return activities.reduce<TimelineGroup<T>[]>((groups, activity) => {
    const label = timelineGroupLabel(activity.date, now);
    const current = groups[groups.length - 1];

    if (current?.label === label) {
      current.activities.push(activity);
    } else {
      groups.push({ label, activities: [activity] });
    }

    return groups;
  }, []);
}

const time = (value: string) => parseDate(value)?.getTime() ?? 0;

// The server's order, newest day first and then latest saved first, so a page reads as the server split it.
export function mergeStageEvents(
  activities: Activity[], stageEvents: TimelineStageEvent[],
): Array<Activity | StageTimelineItem> {
  const items = stageEvents.map(stageEvent => ({ date: stageEvent.day, stageEvent }));
  // On a tie a stage change goes first, as Os::ActivityHelper#os_activity_entries orders the deal's own feed.
  const isNewer = (item: StageTimelineItem, activity: Activity) => (item.date === activity.date
    ? time(item.stageEvent.startTime) >= time(activity.createdAt)
    : item.date > activity.date);

  const merged: Array<Activity | StageTimelineItem> = [];
  let next = 0;
  activities.forEach((activity) => {
    while (next < items.length && isNewer(items[next], activity)) merged.push(items[next++]);
    merged.push(activity);
  });

  return [...merged, ...items.slice(next)];
}

// An activity only carries a calendar date, so the clock time is known only when it was logged on
// the day it happened (createdAt falls on that date)
function loggedAt(activity: Pick<Activity, 'date' | 'createdAt'>): Date | null {
  const day = parseDate(activity.date);
  const created = parseDate(activity.createdAt);

  return day && created && isSameDay(day, created) ? created : null;
}

// "5m ago" / "7h ago" for today, "3d ago" within the week, then "Sep 3" or "Sep 3, 2025"
export function timelineTimeLabel(activity: Pick<Activity, 'date' | 'createdAt'>, now: Date = new Date()): string {
  const day = parseDate(activity.date);
  if (!day) return '';

  const time = loggedAt(activity);
  if (time && isSameDay(time, now)) {
    const minutes = Math.floor((now.getTime() - time.getTime()) / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;

    return `${Math.floor(minutes / 60)}h ago`;
  }

  const days = diffDays(now, day);
  if (days === 0) return 'Today';
  if (days > 0 && days < 7) return `${days}d ago`;

  return formatTokens(day, day.getFullYear() === now.getFullYear() ? 'MMM D' : 'MMM D, YYYY');
}

// Full date for the time label's tooltip, e.g. "Sun, Sep 27, 2026, 1:08 PM"
export function timelineTimeTitle(activity: Pick<Activity, 'date' | 'createdAt'>): string {
  const time = loggedAt(activity);
  if (time) return formatTokens(time, 'ddd, MMM D, YYYY, h:mm A');

  const day = parseDate(activity.date);
  return day ? formatTokens(day, 'ddd, MMM D, YYYY') : '';
}
